import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import {
  Modal,
  Box,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Typography,
  Chip,
  OutlinedInput,
  Alert,
  AlertTitle,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { postFeatureConfiguration } from "../../../store/actions";

// AWS Regions list
const awsRegionsList = [
  { code: "us-east-1", name: "US East (N. Virginia)" },
  { code: "us-east-2", name: "US East (Ohio)" },
  { code: "us-west-1", name: "US West (N. California)" },
  { code: "us-west-2", name: "US West (Oregon)" },
  { code: "eu-west-1", name: "Europe (Ireland)" },
  { code: "eu-west-2", name: "Europe (London)" },
  { code: "eu-west-3", name: "Europe (Paris)" },
  { code: "eu-central-1", name: "Europe (Frankfurt)" },
  { code: "eu-north-1", name: "Europe (Stockholm)" },
  { code: "ap-southeast-1", name: "Asia Pacific (Singapore)" },
  { code: "ap-southeast-2", name: "Asia Pacific (Sydney)" },
  { code: "ap-southeast-3", name: "Asia Pacific (Jakarta)" },
  { code: "ap-northeast-1", name: "Asia Pacific (Tokyo)" },
  { code: "ap-northeast-2", name: "Asia Pacific (Seoul)" },
  { code: "ap-northeast-3", name: "Asia Pacific (Osaka)" },
  { code: "ap-south-1", name: "Asia Pacific (Mumbai)" },
  { code: "ap-south-2", name: "Asia Pacific (Hyderabad)" },
  { code: "ap-east-1", name: "Asia Pacific (Hong Kong)" },
  { code: "sa-east-1", name: "South America (São Paulo)" },
  { code: "ca-central-1", name: "Canada (Central)" },
  { code: "me-south-1", name: "Middle East (Bahrain)" },
  { code: "me-central-1", name: "Middle East (UAE)" },
  { code: "af-south-1", name: "Africa (Cape Town)" },
  { code: "eu-south-1", name: "Europe (Milan)" },
  { code: "eu-south-2", name: "Europe (Spain)" },
];

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(3, 4, 4),
    borderRadius: "8px",
    maxWidth: "700px",
    width: "100%",
    maxHeight: "90vh",
    overflow: "auto",
    position: "relative",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    marginLeft: "auto",
    marginRight: -12,
    marginTop: -12,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  submitButton: {
    marginTop: theme.spacing(3),
    backgroundColor: "#F98125",
    "&:hover": {
      backgroundColor: "#e27021",
    },
  },
}));

const addonDisplayNames = {
  "Event Insight": "Cloud Event",
  "Cost Insight": "FinOps",
  "Cost Optimization": "Cost Optimization",
};

const BucketPolicyModal = (props) => {
  const classes = useStyles();
  const {
    isOpen,
    closeModal,
    account, // Account object from accounts call
    addOnsName,
    allAccounts = [], // All accounts for member selection
    isPostingFeatureConfiguration,
  } = props;

  const [formData, setFormData] = useState({
    role: "",
    accountNumber: (account && account.accountNumber) ? account.accountNumber : "",
    bucketName: "",
    selectedRegion: "",
    processingMode: "",
    selectedMemberAccounts: [],
  });

  const [errors, setErrors] = useState({});

  // Compute derived values safely (before early return)
  const isMaster = account?.is_master === true;
  const accountNumber = account?.accountNumber || "";

  // Filter valid accounts for Event Insight member selection
  const validAccounts = React.useMemo(() => {
    if (addOnsName !== "Event Insight" || !isMaster || !Array.isArray(allAccounts) || !accountNumber) {
      return [];
    }
    return allAccounts.filter((acc) => {
      if (!acc || !acc.accountNumber) return false;
      const isNotMaster = acc.accountNumber !== accountNumber;
      const eventInsightAddon = acc.subscription?.addons?.find(
        (addon) => addon && addon.name === "Event Insight"
      );
      const isEventInsightDisabled =
        !eventInsightAddon || eventInsightAddon.enabled === false;
      const isNotInit = !acc.accountNumber.startsWith("INIT");
      return isNotMaster && isEventInsightDisabled && isNotInit;
    });
  }, [addOnsName, isMaster, allAccounts, accountNumber]);

  // Pre-fill form with data from account's subscription.addons
  useEffect(() => {
    if (isOpen && account && account.accountNumber && addOnsName) {
      // Pre-fill form with data from account's subscription.addons
      const addon = account?.subscription?.addons?.find(
        (addon) => addon && addon.name === addOnsName
      );
      
      console.log("BucketPolicyModal - Pre-filling form with addon data:", {
        addOnsName,
        addon,
        accountNumber: account.accountNumber,
      });
      
      setFormData({
        role: addon?.replicationRole && addon.replicationRole !== "NA" && addon.replicationRole !== "" ? addon.replicationRole : "",
        accountNumber: account.accountNumber || "",
        bucketName: addon?.bucketName && addon.bucketName !== "NA" && addon.bucketName !== "" ? addon.bucketName : "",
        selectedRegion: addon?.region && addon.region !== "NA" && addon.region !== "" ? addon.region : "",
        processingMode: "",
        selectedMemberAccounts: [],
      });
      setErrors({});
    }
  }, [isOpen, account, addOnsName]);

  // Handle success
  useEffect(() => {
    if (props.postFeatureConfigurationMessage) {
      closeModal();
      if (props.setNotificationSent) {
        props.setNotificationSent(false);
      }
    }
  }, [props.postFeatureConfigurationMessage, closeModal, props]);

  // Safety check - return null if account is not provided (after all hooks)
  if (!isOpen || !account || !account.accountNumber || !closeModal) {
    return null;
  }

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleMemberAccountsChange = (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      selectedMemberAccounts: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const validateField = (field, value) => {
    let error = null;

    switch (field) {
      case "role":
        if (!value) {
          error = "Role is required";
        } else if (!/^arn:aws:iam::[0-9]{12}:role/.test(value)) {
          error = "IAM Role must be in proper format";
        }
        break;
      case "accountNumber":
        if (!value) {
          error = "Account number is required";
        } else if (!/^[0-9]+$/.test(value) || value.length !== 12) {
          error = "Account number must be exactly 12 digits and only numbers";
        }
        break;
      case "bucketName":
        if (!value) {
          error = "Bucket name is required";
        } else if (!/^arn:aws:s3:::[a-z0-9]([a-z0-9.-]{1,61}[a-z0-9])?$/.test(value)) {
          error = "S3 bucket name must be in proper format";
        }
        break;
      case "selectedRegion":
        if (!value) {
          error = "AWS Region is required";
        }
        break;
      case "processingMode":
        if (addOnsName === "Cost Insight" && isMaster && !value) {
          error = "Processing mode is required";
        }
        break;
      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach((field) => {
      if (field === "selectedMemberAccounts") return; // Optional field
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    // Construct payload
    const payload = {
      role: formData.role,
      accountNumber: formData.accountNumber,
      bucketName: formData.bucketName,
      readOnlyRole: account?.iamrole || "",
      selectedRegion: formData.selectedRegion,
      module: addOnsName,
      externalId: account?.externalid || account?.externalId || "",
    };

    // Add conditional fields
    if (addOnsName === "Cost Insight" && isMaster) {
      payload.processingMode = formData.processingMode;
    }

    if (addOnsName === "Event Insight" && isMaster) {
      payload.memberAccountNumbers = formData.selectedMemberAccounts.join(",");
    }

    console.log("Bucket Policy Modal Payload:", payload);
    props.postFeatureConfiguration(payload);
  };

  const addonDisplayName = addonDisplayNames[addOnsName] || addOnsName;
  const showProcessingMode = addOnsName === "Cost Insight" && isMaster;
  const showMemberAccounts =
    addOnsName === "Event Insight" && isMaster && validAccounts.length > 0;

  return (
    <Modal
      open={true}
      onClose={closeModal}
      className={classes.modal}
    >
      <Box className={classes.paper}>
        <div className={classes.header}>
          <Typography variant="h6">Verify Bucket Policy</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={closeModal}
          >
            <CloseIcon />
          </IconButton>
        </div>

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Verify Bucket Policy for Additional Addons: {addonDisplayName} for Account
          Number: {accountNumber}
        </Typography>

        {props.postFeatureConfigurationError && (
          <Alert 
            severity="error" 
            sx={{ marginTop: 2, marginBottom: 2 }}
          >
            <AlertTitle>Error</AlertTitle>
            {typeof props.postFeatureConfigurationError === 'string' 
              ? props.postFeatureConfigurationError 
              : props.postFeatureConfigurationError?.message || 'An error occurred while configuring the feature. Please try again.'}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Role ARN */}
          <TextField
            fullWidth
            label="Role ARN*"
            value={formData.role}
            onChange={handleInputChange("role")}
            error={!!errors.role}
            helperText={errors.role}
            className={classes.formField}
            required
          />

          {/* Account Number */}
          <TextField
            fullWidth
            label="Account Number*"
            value={formData.accountNumber}
            disabled
            error={!!errors.accountNumber}
            helperText={errors.accountNumber}
            className={classes.formField}
            required
          />

          {/* S3 Bucket ARN */}
          <TextField
            fullWidth
            label="S3 Bucket ARN*"
            value={formData.bucketName}
            onChange={handleInputChange("bucketName")}
            error={!!errors.bucketName}
            helperText={errors.bucketName}
            className={classes.formField}
            required
          />

          {/* S3 AWS Region */}
          <FormControl
            fullWidth
            error={!!errors.selectedRegion}
            className={classes.formField}
            required
          >
            <InputLabel>S3 AWS Region*</InputLabel>
            <Select
              value={formData.selectedRegion}
              onChange={handleInputChange("selectedRegion")}
              label="S3 AWS Region*"
            >
              {awsRegionsList.map((region) => (
                <MenuItem key={region.code} value={region.code}>
                  {region.name} — {region.code}
                </MenuItem>
              ))}
            </Select>
            {errors.selectedRegion && (
              <FormHelperText>{errors.selectedRegion}</FormHelperText>
            )}
          </FormControl>

          {/* Processing Mode - Conditional */}
          {showProcessingMode && (
            <FormControl
              fullWidth
              error={!!errors.processingMode}
              className={classes.formField}
              required
            >
              <InputLabel>Processing Mode*</InputLabel>
              <Select
                value={formData.processingMode}
                onChange={handleInputChange("processingMode")}
                label="Processing Mode*"
                MenuProps={{
                  PaperProps: {
                    style: {
                      minWidth: 320,
                    },
                  },
                }}
              >
                <MenuItem value="">Select Processing Mode*</MenuItem>
                <MenuItem value="MASTER_AND_ALL_MEMBERS">
                  All Organization accounts
                </MenuItem>
                <MenuItem value="MASTER_AND_ONBOARDED_MEMBERS_ONLY">
                  Onboarded Accounts Only
                </MenuItem>
              </Select>
              {errors.processingMode && (
                <FormHelperText>{errors.processingMode}</FormHelperText>
              )}
            </FormControl>
          )}

          {/* Member Accounts - Conditional */}
          {showMemberAccounts && (
            <FormControl fullWidth className={classes.formField}>
              <InputLabel>Member Accounts</InputLabel>
              <Select
                multiple
                value={formData.selectedMemberAccounts}
                onChange={handleMemberAccountsChange}
                input={<OutlinedInput label="Member Accounts" />}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => {
                      const account = validAccounts.find(
                        (acc) => acc.accountNumber === value
                      );
                      return (
                        <Chip
                          key={value}
                          label={
                            account
                              ? `${account.accountName} (${account.accountNumber})`
                              : value
                          }
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {validAccounts.map((account) => (
                  <MenuItem
                    key={account.accountNumber}
                    value={account.accountNumber}
                  >
                    {account.accountName} ({account.accountNumber})
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                Please make sure you have already created replication role for all
                the member accounts you want to enable Cloud Event for and they
                should be linked to this Master Account
              </FormHelperText>
            </FormControl>
          )}

          <Button
            type="submit"
            variant="contained"
            fullWidth
            className={classes.submitButton}
            disabled={isPostingFeatureConfiguration}
          >
            Submit
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const {
    isPostingFeatureConfiguration,
    postFeatureConfigurationError,
    postFeatureConfigurationMessage,
  } = state.orgAccount;

  return {
    isPostingFeatureConfiguration,
    postFeatureConfigurationError,
    postFeatureConfigurationMessage,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    postFeatureConfiguration: (payload) => {
      dispatch(postFeatureConfiguration(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BucketPolicyModal);

